/**
 * Scheduling Event Dialog
 * Add/Edit event modal for calendar
 */
import { useState, useMemo } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Calendar } from "../ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Loader2, Bell, Trash2, Video, Copy, ExternalLink, CalendarDays, X } from "lucide-react";
import { EVENT_TYPES } from "./constants";
import { toast } from "sonner";
import AddressAutocomplete from "../AddressAutocomplete";
import { format, parseISO } from "date-fns";

export const SchedulingEventDialog = ({
  open,
  onOpenChange,
  eventForm,
  setEventForm,
  editingEvent,
  saving,
  onSave,
  onDelete,
  programs,
  teams,
  locationOptions,
  googleCalendarConnected = false
}) => {
  // Determine if the current location value matches a predefined option
  const isCustomLocation = useMemo(() => {
    if (!eventForm.location) return false;
    if (eventForm.location === "google_meet") return false;
    return !locationOptions.some(loc => loc.value === eventForm.location);
  }, [eventForm.location, locationOptions]);

  // Track whether user chose "custom" from the dropdown
  const [showCustomLocation, setShowCustomLocation] = useState(false);

  // Reset custom location state when dialog opens/closes
  const handleOpenChange = (newOpen) => {
    if (!newOpen) setShowCustomLocation(false);
    onOpenChange(newOpen);
  };

  // When selecting from the location dropdown
  const handleLocationSelect = (v) => {
    if (v === "custom") {
      setShowCustomLocation(true);
      setEventForm({ ...eventForm, location: "" });
    } else if (v === "none" || v === "") {
      setShowCustomLocation(false);
      setEventForm({ ...eventForm, location: "" });
    } else {
      setShowCustomLocation(false);
      setEventForm({ ...eventForm, location: v });
    }
  };

  // Determine the select value to display
  const locationSelectValue = useMemo(() => {
    if (showCustomLocation || isCustomLocation) return "custom";
    if (!eventForm.location) return "none";
    return eventForm.location;
  }, [showCustomLocation, isCustomLocation, eventForm.location]);

  const selectedDateStrings = useMemo(() => (
    [eventForm.date, ...(eventForm.additional_dates || [])]
      .filter(Boolean)
      .filter((value, index, dates) => dates.indexOf(value) === index)
      .sort()
  ), [eventForm.date, eventForm.additional_dates]);

  const selectedDates = useMemo(() => (
    selectedDateStrings.map((value) => parseISO(value))
  ), [selectedDateStrings]);

  const updateSelectedDates = (dates = []) => {
    const values = dates
      .map((date) => format(date, "yyyy-MM-dd"))
      .filter((value, index, allDates) => allDates.indexOf(value) === index)
      .sort();
    setEventForm({
      ...eventForm,
      date: values[0] || "",
      additional_dates: values.slice(1),
    });
  };

  const removeSelectedDate = (dateString) => {
    updateSelectedDates(selectedDates.filter((date) => format(date, "yyyy-MM-dd") !== dateString));
  };

  // Format a date string nicely for display
  const formatDateLabel = (dateStr) => {
    if (!dateStr) return "";
    try {
      return format(parseISO(dateStr), "EEE, MMM d");
    } catch {
      return dateStr;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl gap-0 overflow-y-auto rounded-[28px] border-[#123f3c]/15 bg-[#f5f1e8] p-0 shadow-[0_28px_80px_rgba(18,63,60,0.24)]">
        <DialogHeader className="rounded-t-[28px] bg-[#123f3c] px-6 py-5 text-left text-white">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#cbe85e]">
            <CalendarDays className="h-4 w-4" /> Schedule operations
          </div>
          <DialogTitle className="text-2xl text-white">{editingEvent ? "Edit event" : "Add event"}</DialogTitle>
          <DialogDescription className="text-white/70">
            {editingEvent ? "Update the event details" : "Create a new event on the calendar"}
          </DialogDescription>
        </DialogHeader>
        
        <div className="scheduling-event-form space-y-5 px-6 py-6 [&_label]:font-semibold [&_label]:text-[#123f3c] [&_textarea]:border-[#123f3c]/15 [&_textarea]:bg-white">
          <style>{`.scheduling-event-form input[type="text"],
.scheduling-event-form input[type="date"],
.scheduling-event-form input[type="time"] {
  background: white;
  border-color: rgba(18, 63, 60, 0.15);
  color: #0f172a;
  color-scheme: light;
}
.scheduling-event-form input[type="date"]::-webkit-calendar-picker-indicator { cursor: pointer; }
.scheduling-event-form input[type="date"]::-webkit-datetime-edit,
.scheduling-event-form input[type="date"]::-webkit-datetime-edit-fields-wrapper,
.scheduling-event-form input[type="time"]::-webkit-datetime-edit,
.scheduling-event-form input[type="time"]::-webkit-datetime-edit-fields-wrapper {
  color: #0f172a;
}
`}</style>
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input
              value={eventForm.title}
              onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
              placeholder="Event title"
              className="rounded-xl"
            />
          </div>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={eventForm.type} onValueChange={(v) => setEventForm({...eventForm, type: v})}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {EVENT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {editingEvent && (
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
                  className="rounded-xl"
                />
              </div>
            )}
          </div>

          {!editingEvent && (
            <div className="rounded-2xl border border-[#123f3c]/15 bg-white p-4">
              <div className="mb-3 flex items-start justify-between gap-4">
                <div>
                  <Label id="event-dates-label">Event dates *</Label>
                  <p className="mt-1 text-sm text-slate-600">Select every date that should receive this event.</p>
                </div>
                <span className="rounded-full bg-[#e8f3d2] px-3 py-1 text-xs font-semibold text-[#123f3c]">
                  {selectedDateStrings.length} selected
                </span>
              </div>
              <Calendar
                mode="multiple"
                selected={selectedDates}
                onSelect={updateSelectedDates}
                aria-labelledby="event-dates-label"
                className="mx-auto w-fit rounded-xl border border-[#123f3c]/10 bg-[#fbfaf7]"
                classNames={{
                  day_selected: "bg-[#123f3c] text-white hover:bg-[#0b302e] hover:text-white focus:bg-[#123f3c] focus:text-white",
                  day_today: "bg-[#e8f3d2] text-[#123f3c]",
                }}
              />
              {selectedDateStrings.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2" aria-label="Selected event dates">
                  {selectedDateStrings.map((dateString) => (
                    <span key={dateString} className="inline-flex items-center gap-1 rounded-full bg-[#123f3c]/8 px-3 py-1.5 text-sm font-medium text-[#123f3c]">
                      {formatDateLabel(dateString)}
                      <button
                        type="button"
                        onClick={() => removeSelectedDate(dateString)}
                        className="ml-1 rounded-full p-0.5 hover:bg-[#123f3c]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#287d74]"
                        aria-label={`Remove ${formatDateLabel(dateString)}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Start Time</Label>
              <Input
                type="time"
                value={eventForm.start_time}
                onChange={(e) => setEventForm({...eventForm, start_time: e.target.value})}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>End Time</Label>
              <Input
                type="time"
                value={eventForm.end_time}
                onChange={(e) => setEventForm({...eventForm, end_time: e.target.value})}
                className="rounded-xl"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Program</Label>
              <Select value={eventForm.program_id || "none"} onValueChange={(v) => setEventForm({...eventForm, program_id: v === "none" ? "" : v, team_id: ""})}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select program" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="none">None</SelectItem>
                  {programs.map(p => (
                    <SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Team</Label>
              <Select 
                value={eventForm.team_id || "none"} 
                onValueChange={(v) => setEventForm({...eventForm, team_id: v === "none" ? "" : v})}
                disabled={!eventForm.program_id}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="none">None</SelectItem>
                  {teams.filter(t => t.program_id === eventForm.program_id).map(t => (
                    <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Location</Label>
            <Select value={locationSelectValue} onValueChange={handleLocationSelect}>
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="none">None</SelectItem>
                {locationOptions.map(loc => (
                  <SelectItem key={loc.value} value={loc.value}>
                    {loc.type === "facility" ? `↳ ${loc.label}` : loc.label}
                  </SelectItem>
                ))}
                <SelectItem value="custom">📍 Other location…</SelectItem>
              </SelectContent>
            </Select>

            {/* Custom location address input */}
            {(showCustomLocation || isCustomLocation) && (
              <div className="mt-2">
                <AddressAutocomplete
                  value={eventForm.location || ""}
                  onChange={(val) => setEventForm({ ...eventForm, location: val })}
                  onPlaceSelect={(place) => {
                    setEventForm({
                      ...eventForm,
                      location: place.name && place.address
                        ? `${place.name}, ${place.address}`
                        : place.address || place.name || ""
                    });
                  }}
                  placeholder="Search for an address or venue…"
                  searchType="all"
                  className="rounded-xl"
                />
              </div>
            )}
            
            {/* Google Meet Info Box */}
            {eventForm.location === "google_meet" && (
              <div className={`mt-2 p-3 rounded-xl border ${googleCalendarConnected ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-start gap-2">
                  <Video className={`w-5 h-5 mt-0.5 ${googleCalendarConnected ? 'text-green-600' : 'text-blue-600'}`} />
                  <div className="flex-1">
                    {eventForm.meet_link ? (
                      <>
                        <p className={`text-sm font-medium ${googleCalendarConnected ? 'text-green-800' : 'text-blue-800'}`}>Google Meet Link</p>
                        <div className="flex items-center gap-2 mt-1">
                          <code className={`text-xs px-2 py-1 rounded flex-1 truncate ${googleCalendarConnected ? 'bg-green-100' : 'bg-blue-100'}`}>
                            {eventForm.meet_link}
                          </code>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => {
                              navigator.clipboard.writeText(eventForm.meet_link);
                              toast.success("Meet link copied!");
                            }}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => window.open(eventForm.meet_link, '_blank')}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </>
                    ) : googleCalendarConnected ? (
                      <>
                        <p className="text-sm font-medium text-green-800">Google Calendar Connected</p>
                        <p className="text-xs text-green-600 mt-0.5">
                          A real Google Meet link will be created and synced to your calendar.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-blue-800">Virtual Meeting</p>
                        <p className="text-xs text-blue-600 mt-0.5">
                          A Meet-style link will be generated. Connect Google Calendar for real Meet links.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={eventForm.notes}
              onChange={(e) => setEventForm({...eventForm, notes: e.target.value})}
              placeholder="Additional notes..."
              className="rounded-xl"
              rows={2}
            />
          </div>

          {!editingEvent && (
            <>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="recurring"
                  checked={eventForm.is_recurring}
                  onChange={(e) => setEventForm({...eventForm, is_recurring: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="recurring" className="cursor-pointer">Recurring event</Label>
              </div>

              {eventForm.is_recurring && (
                <div className="grid grid-cols-1 gap-4 border-l-2 border-[#287d74] pl-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Repeat</Label>
                    <Select value={eventForm.recurrence_pattern} onValueChange={(v) => setEventForm({...eventForm, recurrence_pattern: v})}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder="Select pattern" />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={eventForm.recurrence_end}
                      onChange={(e) => setEventForm({...eventForm, recurrence_end: e.target.value})}
                      className="rounded-xl"
                    />
                  </div>
                </div>
              )}
            </>
          )}

          <div className="flex items-center gap-2 pt-2 border-t">
            <input
              type="checkbox"
              id="notify"
              checked={eventForm.send_notification}
              onChange={(e) => setEventForm({...eventForm, send_notification: e.target.checked})}
              className="rounded"
            />
            <Label htmlFor="notify" className="cursor-pointer flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Send notification to participants
            </Label>
          </div>
        </div>

        <DialogFooter className="sticky bottom-0 border-t border-[#123f3c]/10 bg-white px-6 py-4">
          {editingEvent && (
            <Button
              variant="outline"
              onClick={() => onDelete(editingEvent.event_id)}
              className="mr-auto rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
          <Button variant="outline" onClick={() => handleOpenChange(false)} className="rounded-2xl border-[#123f3c]/20 text-[#123f3c]">
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving} className="rounded-2xl bg-[#123f3c] text-white hover:bg-[#0b302e]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {editingEvent ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SchedulingEventDialog;
